import type { jsPDF } from 'jspdf';

/**
 * Converts markdown to plain text for PDF rendering.
 * Does NOT process table blocks — those are handled by parseContentSegments.
 * jsPDF does not render markdown — raw symbols appear literally without this step.
 */
export function stripMarkdownForPdf(text: string): string {
  return text
    .replace(/```(?:\w+)?\n?([\s\S]*?)```/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+(.+)$/gm, '$1')
    .replace(/\*{3}(.+?)\*{3}/g, '$1')
    .replace(/_{3}(.+?)_{3}/g, '$1')
    .replace(/\*{2}(.+?)\*{2}/g, '$1')
    .replace(/_{2}(.+?)_{2}/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, (m) => m.trimStart())
    .replace(/^[-*_]{3,}\s*$/gm, '---')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Replaces characters outside the Windows-1252 range that jsPDF's standard
 * Helvetica font cannot encode, preventing garbled output in exported PDFs.
 * Newlines and tabs are preserved for text layout.
 */
export function sanitizeForPdf(text: string): string {
  return text
    .replace(/[""„‟]/g, '"')
    .replace(/[''‚‛]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/ /g, ' ')
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
    .replace(/[☀-➿]/g, '')
    .replace(/[^\t\n\r -ÿ]/g, '');
}

// ─── Color / layout types ─────────────────────────────────────────────────────

export type RgbColor = [number, number, number];

export const PDF_COLORS = {
  brand:  [29, 115, 185]  as RgbColor, // hsl(207 73% 42%) — Prodgy primary
  dark:   [17, 24, 39]    as RgbColor, // gray-900
  mid:    [107, 114, 128] as RgbColor, // gray-500
  light:  [229, 231, 235] as RgbColor, // gray-200
  userBg: [233, 243, 252] as RgbColor, // hsl(207 73% 95%) — Prodgy tint
  aiBg:   [249, 250, 251] as RgbColor, // gray-50
};

export type PdfColors = typeof PDF_COLORS;

export interface PdfLayout {
  margin:     number;
  contentW:   number;
  pageWidth:  number;
  pageHeight: number;
  footerH:    number;
  bottomEdge: number;
}

// ─── Segment types ────────────────────────────────────────────────────────────

export interface PdfTextSegment  { type: 'text';  content: string }
export interface PdfTableSegment { type: 'table'; headers: string[]; rows: string[][] }
export type PdfSegment = PdfTextSegment | PdfTableSegment;

// ─── Markdown table parser ────────────────────────────────────────────────────

function parseTableRow(line: string): string[] {
  return line
    .split('|')
    .slice(1, -1)
    .map((cell) => stripMarkdownForPdf(sanitizeForPdf(cell.trim())));
}

const TABLE_ROW_RE = /^\s*\|.+\|\s*$/;
const TABLE_SEP_RE = /^\s*\|[\s\-:|]+\|\s*$/;

/**
 * Splits message content into text and table segments.
 * sanitizeForPdf and stripMarkdownForPdf are applied to all text content.
 */
export function parseContentSegments(rawContent: string): PdfSegment[] {
  const segments: PdfSegment[] = [];
  const lines = rawContent.split('\n');
  let textLines: string[] = [];
  let i = 0;

  const flushText = () => {
    const block = textLines.join('\n').trim();
    if (block) segments.push({ type: 'text', content: stripMarkdownForPdf(sanitizeForPdf(block)) });
    textLines = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    // Markdown table (header row followed by separator)
    if (TABLE_ROW_RE.test(line) && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      flushText();
      const headers = parseTableRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW_RE.test(lines[i])) {
        rows.push(parseTableRow(lines[i]));
        i++;
      }
      segments.push({ type: 'table', headers, rows });
      continue;
    }

    textLines.push(line);
    i++;
  }

  flushText();
  return segments;
}

// ─── PDF rendering helpers ────────────────────────────────────────────────────

export function addPage(doc: jsPDF, layout: PdfLayout, colors: PdfColors): void {
  doc.addPage();
  doc.setDrawColor(...colors.light);
  doc.setLineWidth(0.3);
  doc.line(layout.margin, 8, layout.pageWidth - layout.margin, 8);
}

export function drawFooters(doc: jsPDF, layout: PdfLayout, colors: PdfColors): void {
  const { pageHeight, footerH, margin, pageWidth } = layout;
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setFillColor(...colors.light);
    doc.rect(0, pageHeight - footerH, pageWidth, footerH, 'F');
    doc.setFontSize(8);
    doc.setTextColor(...colors.mid);
    doc.setFont('helvetica', 'normal');
    doc.text('Prodgy - prodgy.io', margin, pageHeight - 4);
    doc.text(`Pagina ${p} de ${total}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }
}

export function renderTable(
  doc: jsPDF,
  layout: PdfLayout,
  colors: PdfColors,
  headers: string[],
  rows: string[][],
  startY: number,
): number {
  const { margin, contentW, bottomEdge } = layout;
  const colCount = Math.max(headers.length, 1);
  const colW     = contentW / colCount;
  const cellPadX = 2.5;
  const cellPadY = 2;
  const lineH    = 4;
  const headerH  = 7;

  let ty = startY;

  if (ty + headerH > bottomEdge) { addPage(doc, layout, colors); ty = 14; }
  headers.forEach((h, ci) => {
    const cx = margin + ci * colW;
    doc.setFillColor(...colors.brand);
    doc.rect(cx, ty, colW, headerH, 'F');
    doc.setDrawColor(...colors.brand);
    doc.setLineWidth(0.1);
    doc.rect(cx, ty, colW, headerH, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    const hLines = doc.splitTextToSize(h, colW - cellPadX * 2);
    doc.text(hLines[0] ?? '', cx + cellPadX, ty + headerH - cellPadY);
  });
  ty += headerH;

  rows.forEach((row, ri) => {
    const rowH = Math.max(
      ...row.map((cell) => {
        const wrapped = doc.splitTextToSize(cell, colW - cellPadX * 2);
        return wrapped.length * lineH + cellPadY * 2;
      }),
      7,
    );

    if (ty + rowH > bottomEdge) { addPage(doc, layout, colors); ty = 14; }

    row.forEach((cell, ci) => {
      const cx = margin + ci * colW;
      doc.setFillColor(...(ri % 2 === 0
        ? ([245, 248, 255] as RgbColor)
        : ([255, 255, 255] as RgbColor)));
      doc.rect(cx, ty, colW, rowH, 'F');
      doc.setDrawColor(...colors.light);
      doc.setLineWidth(0.15);
      doc.rect(cx, ty, colW, rowH, 'S');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.dark);
      const wrapped = doc.splitTextToSize(cell, colW - cellPadX * 2);
      wrapped.forEach((ln: string, li: number) => {
        doc.text(ln, cx + cellPadX, ty + cellPadY + 3.5 + li * lineH);
      });
    });
    ty += rowH;
  });

  return ty + 3;
}

export function renderHeader(
  doc: jsPDF,
  layout: PdfLayout,
  colors: PdfColors,
  wsName: string,
  dateStr: string,
): number {
  const { margin, pageWidth } = layout;
  let y = margin;

  doc.setFillColor(...colors.brand);
  doc.rect(0, 0, pageWidth, 2, 'F');

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text(`Chat - ${wsName}`, margin, y + 10);
  y += 14;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.mid);
  doc.text(`Exportado em: ${dateStr}`, margin, y);
  y += 8;

  doc.setDrawColor(...colors.light);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  return y;
}

export function renderMessageBlock(
  doc: jsPDF,
  layout: PdfLayout,
  colors: PdfColors,
  segments: PdfSegment[],
  label: string,
  isUser: boolean,
  startY: number,
): number {
  const { margin, contentW, bottomEdge } = layout;
  const bgColor  = isUser ? colors.userBg : colors.aiBg;
  const labelClr = isUser ? colors.brand  : colors.mid;
  const hasTable = segments.some((s) => s.type === 'table');
  let y = startY;

  if (!hasTable) {
    const lines = doc.splitTextToSize(
      segments.map((s) => (s.type === 'text' ? s.content : '')).join('\n').trim(),
      contentW - 8,
    );
    const blockH = 5 + lines.length * 4.5 + 6;
    if (y + blockH > bottomEdge) { addPage(doc, layout, colors); y = 14; }

    doc.setFillColor(...bgColor);
    doc.roundedRect(margin, y, contentW, blockH, 2, 2, 'F');
    doc.setFillColor(...(isUser ? colors.brand : colors.light));
    doc.rect(margin, y, 1.5, blockH, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...labelClr);
    doc.text(label.toUpperCase(), margin + 5, y + 4.5);

    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.dark);
    let lineY = y + 9;
    lines.forEach((line: string) => {
      if (lineY > bottomEdge) { addPage(doc, layout, colors); lineY = 14; }
      doc.text(line, margin + 5, lineY);
      lineY += 4.5;
    });
    y = lineY + 4;
  } else {
    if (y + 10 > bottomEdge) { addPage(doc, layout, colors); y = 14; }
    doc.setFillColor(...bgColor);
    doc.roundedRect(margin, y, contentW, 8, 2, 2, 'F');
    doc.setFillColor(...(isUser ? colors.brand : colors.light));
    doc.rect(margin, y, 1.5, 8, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...labelClr);
    doc.text(label.toUpperCase(), margin + 5, y + 5.5);
    y += 10;

    for (const seg of segments) {
      if (seg.type === 'text' && seg.content.trim()) {
        const lines = doc.splitTextToSize(seg.content, contentW - 8);
        lines.forEach((line: string) => {
          if (y > bottomEdge) { addPage(doc, layout, colors); y = 14; }
          doc.setFontSize(9.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.dark);
          doc.text(line, margin + 5, y);
          y += 4.5;
        });
        y += 2;
      } else if (seg.type === 'table') {
        if (y + 14 > bottomEdge) { addPage(doc, layout, colors); y = 14; }
        y = renderTable(doc, layout, colors, seg.headers, seg.rows, y);
      }
    }
    y += 2;
  }

  return y;
}
